import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Phone, Send } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BUSINESS } from "@/data/site";
import { WhatsAppIcon } from "@/components/Icons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnquiryForm() {
  const { t, lang } = useLanguage();
  const c = t.contactPage;
  const [form, setForm] = useState({ name: "", mobile: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (form.name.trim().length < 2) return false;
    const digits = form.mobile.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "");
    if (!/^\d{10}$/.test(digits)) return false;
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return false;
    if (form.message.trim().length < 5) return false;
    return true;
  };

  const waText = {
    en: `Namaste KAZIM'S! I would like to enquire about your tea.\nName: ${form.name}\nMobile: ${form.mobile}\n${form.email ? `Email: ${form.email}\n` : ""}Message: ${form.message}`,
    hi: `नमस्ते KAZIM'S! मैं आपकी चाय के बारे में पूछताछ करना चाहता/चाहती हूँ।\nनाम: ${form.name}\nमोबाइल: ${form.mobile}\n${form.email ? `ईमेल: ${form.email}\n` : ""}संदेश: ${form.message}`,
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!validate()) {
      setError(c.formError);
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("bad status");
      setStatus("success");
    } catch {
      setStatus("error");
      setError(c.errorB);
    }
  };

  const reset = () => {
    setForm({ name: "", mobile: "", email: "", message: "" });
    setStatus("idle");
    setError("");
  };

  const inputCls =
    "w-full border hairline bg-white/[0.03] px-4 py-3.5 text-sm text-cream placeholder:text-cream/35 transition-colors focus:border-gold/60";

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="border hairline bg-velvet/60 p-8 sm:p-10"
        data-testid="enquiry-success-panel"
      >
        <CheckCircle2 className="h-10 w-10 text-gold" strokeWidth={1.25} />
        <h3 className="mt-5 font-serif text-2xl text-cream">{c.successT}</h3>
        <p className="mt-3 text-sm leading-relaxed text-cream/70">{c.successB}</p>
        <div className="mt-7 flex flex-wrap gap-3">
          <a
            href={BUSINESS.whatsappEnquiry(waText[lang])}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="enquiry-whatsapp-handoff-button"
            className="inline-flex items-center gap-2.5 bg-gold px-6 py-3 text-sm font-medium uppercase tracking-[0.12em] text-obsidian transition-all duration-300 hover:bg-gold-light"
          >
            <WhatsAppIcon className="h-4 w-4" />
            {c.viaWa}
          </a>
          <button
            onClick={reset}
            data-testid="enquiry-send-another-button"
            className="inline-flex items-center gap-2 border border-gold/40 px-6 py-3 text-sm uppercase tracking-[0.12em] text-gold transition-all duration-300 hover:bg-gold/10"
          >
            {c.send}
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate data-testid="enquiry-form" className="border hairline bg-velvet/60 p-8 sm:p-10">
      <h3 className="font-serif text-2xl text-cream sm:text-3xl">{c.formTitle}</h3>
      <p className="mt-2.5 text-sm leading-relaxed text-cream/60">{c.formBody}</p>

      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="enq-name" className="mb-2 block text-[11px] font-medium uppercase tracking-[0.18em] text-gold/90">
            {c.name}
          </label>
          <input id="enq-name" data-testid="contact-name-input" className={inputCls} placeholder={c.namePh} value={form.name} onChange={set("name")} />
        </div>
        <div>
          <label htmlFor="enq-mobile" className="mb-2 block text-[11px] font-medium uppercase tracking-[0.18em] text-gold/90">
            {c.mobile}
          </label>
          <input id="enq-mobile" data-testid="contact-mobile-input" type="tel" inputMode="numeric" className={inputCls} placeholder={c.mobilePh} value={form.mobile} onChange={set("mobile")} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="enq-email" className="mb-2 block text-[11px] font-medium uppercase tracking-[0.18em] text-gold/90">
            {c.email} <span className="text-cream/40 normal-case tracking-normal">{c.emailOpt}</span>
          </label>
          <input id="enq-email" data-testid="contact-email-input" type="email" className={inputCls} placeholder={c.emailPh} value={form.email} onChange={set("email")} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="enq-message" className="mb-2 block text-[11px] font-medium uppercase tracking-[0.18em] text-gold/90">
            {c.message}
          </label>
          <textarea id="enq-message" data-testid="contact-message-input" rows={4} className={`${inputCls} resize-none`} placeholder={c.messagePh} value={form.message} onChange={set("message")} />
        </div>
      </div>

      {error && (
        <p data-testid="enquiry-error-message" className="mt-4 border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-gold-light">
          {error}
        </p>
      )}

      <div className="mt-8 flex flex-wrap gap-3">
        <button
          type="submit"
          disabled={status === "sending"}
          data-testid="contact-enquiry-submit-button"
          className="inline-flex items-center gap-2.5 bg-gold px-8 py-3.5 text-sm font-medium uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_28px_rgba(212,175,55,0.3)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Send className="h-4 w-4" strokeWidth={1.5} />
          {status === "sending" ? c.sending : c.send}
        </button>
        <a
          href={BUSINESS.whatsappEnquiry(waText[lang])}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="enquiry-whatsapp-direct-button"
          className="inline-flex items-center gap-2.5 border border-gold/50 px-6 py-3.5 text-sm uppercase tracking-[0.15em] text-gold transition-all duration-300 hover:border-gold hover:bg-gold/10"
        >
          <WhatsAppIcon className="h-4 w-4" />
          {c.viaWa}
        </a>
      </div>

      {status === "error" && (
        <a
          href={BUSINESS.tel}
          data-testid="enquiry-call-fallback-link"
          className="mt-4 inline-flex items-center gap-2 text-sm text-cream/60 transition-colors hover:text-gold"
        >
          <Phone className="h-4 w-4" strokeWidth={1.5} />
          {BUSINESS.phoneDisplay}
        </a>
      )}
    </form>
  );
}
